import Vue from "vue";
import LineChart from "@/components/LineChart.vue";
import moment from "moment";
import { getPulseTrackTheme, hexToRgba, } from "@/components/PulseInsights/pulseTracks";
// The three track lines, in draw/legend order. Which one is emphasised (solid
// + filled) is driven by the `selectedKey` prop; the rest render as dashed
// overlays.
const TRACK_SERIES = [
    { key: "mind", slug: "pulse_well_in_mind" },
    { key: "body", slug: "pulse_well_in_body" },
    { key: "life", slug: "pulse_well_in_life" },
];
// Height of the chart area, in px. Width is supplied by the parent (see props).
const CHART_HEIGHT = 300;
export default Vue.extend({
    name: "PulseTrendChart",
    components: { LineChart },
    props: {
        // the raw get-trends series
        trends: { type: Array, default: () => [] },
        // optional heading above the chart; parents that supply their own
        // section title can pass "" to hide it
        title: { type: String, default: "" },
        // CSS width of the chart card; parents size it via their own layout
        width: { type: String, default: "100%" },
        // drop the card chrome (border/shadow/padding) when the parent supplies
        // its own container
        bare: { type: Boolean, default: false },
        // which track line is brought into focus (solid + filled); the others
        // render as dashed overlays. Defaults to Mind.
        selectedKey: { type: String, default: "mind" },
        // the track slugs still available to this user; only these lines are
        // drawn. null (unset) draws all three (backwards compatible).
        availableSlugs: { type: Array, default: null },
    },
    data() {
        return {
            chartHeight: CHART_HEIGHT,
        };
    },
    computed: {
        chartStyles() {
            // applied to vue-chartjs' wrapper div; fixes the height independently
            return { position: "relative", height: `${this.chartHeight}px` };
        },
        // trend points within the last 12 months, oldest first
        points() {
            const cutoff = moment().subtract(12, "months");
            return this.trends
                .filter((point) => moment(point.date).isSameOrAfter(cutoff))
                .slice()
                .sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
        },
        // the day keys that form the category axis
        chartDays() {
            return this.points.map((point) => point.date);
        },
        // a month label ("MMM") at the first day of each month, capped to <= 6
        // (e.g. every other month across a full year); "" everywhere else
        monthTickLabels() {
            const days = this.chartDays;
            const labels = new Array(days.length).fill("");
            const monthStartIndexes = [];
            let lastMonth = "";
            days.forEach((key, index) => {
                const month = key.slice(0, 7); // YYYY-MM
                if (month !== lastMonth) {
                    monthStartIndexes.push(index);
                    lastMonth = month;
                }
            });
            const step = Math.max(1, Math.ceil(monthStartIndexes.length / 6));
            monthStartIndexes.forEach((dayIndex, n) => {
                if (n % step === 0)
                    labels[dayIndex] = moment(days[dayIndex]).format("MMM");
            });
            return labels;
        },
        // the track lines to draw — TRACK_SERIES filtered to the slugs the user
        // still has (a track removed from their plan drops off the graph entirely)
        visibleSeries() {
            const slugs = this.availableSlugs;
            if (!slugs)
                return TRACK_SERIES;
            return TRACK_SERIES.filter((series) => slugs.indexOf(series.slug) !== -1);
        },
        chartData() {
            const labels = this.chartDays.map((key) => moment(key).format("DD MMM"));
            const datasets = this.visibleSeries.map((series) => {
                const theme = getPulseTrackTheme(series.slug);
                const isSelected = series.key === this.selectedKey;
                return {
                    label: theme.shortLabel,
                    data: this.points.map((point) => {
                        const value = point[series.key];
                        return value === null || value === undefined ? null : value;
                    }),
                    borderColor: theme.color,
                    backgroundColor: isSelected
                        ? hexToRgba(theme.color, 0.12)
                        : "transparent",
                    pointBackgroundColor: theme.color,
                    pointRadius: isSelected ? 4 : 3,
                    pointHoverRadius: 5,
                    borderWidth: isSelected ? 3 : 2,
                    borderDash: isSelected ? [] : [6, 4],
                    fill: isSelected,
                    lineTension: 0.4,
                    spanGaps: true,
                };
            });
            return { labels, datasets };
        },
        chartOptions() {
            // capture for the tick callback (one month label per index, "" elsewhere)
            const monthTickLabels = this.monthTickLabels;
            // capture for the click/hover handlers below. Chart.js rebinds `this` to
            // the chart instance inside onClick/onHover, so the day keys and the emit
            // helper are closed over here rather than read off `this` at call time.
            const chartDays = this.chartDays;
            const emitPointClick = (date) => {
                this.$emit("point-click", date);
            };
            return {
                responsive: true,
                // height comes from the wrapper div (chartStyles); width fills the card
                maintainAspectRatio: false,
                // clicking a point (or anywhere in its day's vertical band) opens the
                // report for that date; `this` here is the Chart.js instance
                onClick(event, elements) {
                    const chart = this;
                    let index = -1;
                    // prefer the whole-day band (index mode) so a near-miss still counts
                    if (typeof chart.getElementsAtEventForMode === "function") {
                        const found = chart.getElementsAtEventForMode(event, "index", {
                            intersect: false,
                        });
                        if (found && found.length)
                            index = found[0]._index;
                    }
                    if (index < 0 && elements && elements.length)
                        index = elements[0]._index;
                    if (index >= 0 && chartDays[index])
                        emitPointClick(chartDays[index]);
                },
                // pointer cursor while hovering a point, so it reads as clickable
                onHover(event, elements) {
                    const native = event.native || event;
                    const target = native && native.target;
                    if (target && target.style)
                        target.style.cursor =
                            elements && elements.length ? "pointer" : "default";
                },
                // keep the last/first point's dot from being clipped at the edges
                layout: { padding: { right: 12, left: 4 } },
                legend: {
                    display: true,
                    position: "top",
                    align: "end",
                    labels: {
                        usePointStyle: true,
                        boxWidth: 8,
                        padding: 16,
                        fontColor: "#9aa1ad",
                    },
                },
                tooltips: { mode: "index", intersect: false },
                scales: {
                    xAxes: [
                        {
                            gridLines: { display: false },
                            ticks: {
                                fontColor: "#9aa1ad",
                                autoSkip: false,
                                maxRotation: 0,
                                callback: (_value, index) => monthTickLabels[index] || "",
                            },
                        },
                    ],
                    yAxes: [
                        {
                            ticks: {
                                min: 0,
                                // a touch of headroom above 100 so a 100 marker (and the line
                                // joining consecutive 100s) clears the top edge instead of
                                // being clipped in half
                                max: 105,
                                stepSize: 20,
                                fontColor: "#9aa1ad",
                            },
                            // Chart.js appends the scale max (105) as a boundary tick, which
                            // draws an unlabelled gridline in the padding. Drop everything
                            // above 100 so the 100 line stays the topmost gridline; the 0–105
                            // value range (the headroom) is unaffected.
                            afterBuildTicks: (scale, ticks) => {
                                const kept = (ticks || scale.ticks || []).filter((tick) => tick <= 100);
                                scale.ticks = kept;
                                return kept;
                            },
                            // translucent so it reads on both light and dark backgrounds
                            gridLines: {
                                color: "rgba(148, 161, 173, 0.2)",
                                drawBorder: false,
                            },
                        },
                    ],
                },
                elements: { line: { tension: 0.4 } },
            };
        },
    },
});
